import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction, enumerated } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import homeVisitStatsAction from './homeVisitStats.action.json' with { type: 'json' }
import { z } from 'zod'
import { dateOrIsoString } from '~/prix/types/date'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { select } from '~/prix/types/presets/select'
import prefixProperties from '~/prix/utils/prefixProperties'
import { citizenHealthProfileShape } from '../../citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import { citizenLocationShape, citizenShapeBase } from '../../citizen/stats/citizenShape'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

const shape = z.object({
  originalId: z.string().describe('ID original'),
  shift: enumerated({
    1: 'Manhã',
    2: 'Tarde',
    3: 'Noite',
  }).describe('Turno da visita'),
  visitDate: z.string().describe('Data da visita'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  professionalCns: z.string().describe('CNS do profissional'),
  professionalCbo: z.string().describe('CBO do profissional'),
  professionalIne: z.string().describe('INE do profissional'),
  professionalCnes: z.string().describe('CNES do profissional'),
  visitReasonIsRegistrationUpdate: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Atualização cadastral'),
  visitReasonIsPeriodicVisit: z.boolean().optional().describe('Motivo da visita: Visita periódica'),
  visitReasonIsActiveSearchForConsultation: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Busca ativa para consulta'),
  visitReasonIsActiveSearchForExamination: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Busca ativa para exame'),
  visitReasonIsActiveSearchForVaccination: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Busca ativa para vacinação'),
  visitReasonIsActiveSearchForBolsaFamiliaConditionalities: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Busca ativa para condicionalidades do Bolsa Família'),
  visitReasonIsPregnantWomanFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de gestante'),
  visitReasonIsPostpartumWomanFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de puérpera'),
  visitReasonIsNewbornFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de recém-nascido'),
  visitReasonIsChildFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de criança'),
  visitReasonIsMalnutritionPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com desnutrição'),
  visitReasonIsRehabilitationOrPersonWithDisabilityFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de reabilitação ou pessoa com deficiência'),
  visitReasonIsHypertensionPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com hipertensão'),
  visitReasonIsDiabetesPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com diabetes'),
  visitReasonIsAsthmaPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com asma'),
  visitReasonIsCopdEmphysemaPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com DPOC/enfisema'),
  visitReasonIsCancerPatientFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de paciente com câncer'),
  visitReasonIsOtherChronicDiseasesPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com outras doenças crônicas'),
  visitReasonIsPersonWithLeprosyFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com hanseníase'),
  visitReasonIsPersonWithTuberculosisFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com tuberculose'),
  visitReasonIsRespiratorySymptomaticPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa sintomática respiratória'),
  visitReasonIsSmokerFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de tabagista'),
  visitReasonIsHomeboundBedriddenPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa acamada ou acamado'),
  visitReasonIsSocialVulnerabilityConditionsFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de condições de vulnerabilidade social'),
  visitReasonIsBolsaFamiliaConditionalitiesFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de condicionalidades do Bolsa Família'),
  visitReasonIsMentalHealthPersonFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de pessoa com saúde mental'),
  visitReasonIsAlcoholUserFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de usuário de álcool'),
  visitReasonIsOtherDrugsUserFollowUp: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Acompanhamento de usuário de outras drogas'),
  visitReasonIsDischargeFromHospitalization: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Alta de internação'),
  visitReasonIsEnvironmentalVectorControl: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Controle ambiental de vetores'),
  visitReasonIsInvitationToCollectiveActivitiesHealthCampaign: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Convite para atividades coletivas/campanha de saúde'),
  visitReasonIsGuidancePrevention: z
    .boolean()
    .optional()
    .describe('Motivo da visita: Orientação/prevenção'),
  visitReasonIsOther: z.boolean().optional().describe('Motivo da visita: Outro'),
  outcomeStatus: enumerated({
    visitConducted: 'Visita realizada',
    visitRefused: 'Visita recusada',
    absent: 'Ausência',
  }).describe('Status da visita'),
  ...prefixProperties('citizen.', citizenShapeBase.shape),
  ...prefixProperties('citizen.', citizenLocationShape.shape),
  ...prefixProperties('citizenHealth.', citizenHealthProfileShape.shape),
})

const expressionForCharacteristics = expression(shape.shape)
const expressionForAssertions = expression(
  shape.pick({
    'citizen.sex': true,
  }).shape,
)

export const homeVisitVariable: Variable = {
  key: 'homeVisit',
  actionDefinitionKey: homeVisitStatsAction.key,
  title: 'Visitas domiciliares',
  categoryKey: 'healthHomeVisit',
  unitSingular: 'registro de visita domiciliar',
  unitPlural: 'registros de visitas domiciliares',
  grammaticalGender: 'female',
  timeType: 'incidence',
  shape: getZodSchemaInfo(shape),
  betterDensity: 'more',
  relevance: healthVariableBaseRelevance.homeVisit,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    /* CIDADÃO */
    'citizen.cpf',
    'citizen.cns',
    'citizen.birthDate',
    'citizen.sex',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.ageRange',
    'citizen.phoneNumber',
    'citizen.originalId',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.deceased',

    /* VISITA */
    'visitDate',
    'shift',
    'outcomeStatus',
    'visitReasonIsRegistrationUpdate',
    'visitReasonIsPeriodicVisit',
    'visitReasonIsActiveSearchForConsultation',
    'visitReasonIsActiveSearchForExamination',
    'visitReasonIsActiveSearchForVaccination',
    'visitReasonIsPregnantWomanFollowUp',
    'visitReasonIsActiveSearchForBolsaFamiliaConditionalities',
    'visitReasonIsNewbornFollowUp',
    'visitReasonIsPostpartumWomanFollowUp',
    'visitReasonIsChildFollowUp',
    'visitReasonIsMalnutritionPersonFollowUp',
    'visitReasonIsRehabilitationOrPersonWithDisabilityFollowUp',
    'visitReasonIsHypertensionPersonFollowUp',
    'visitReasonIsDiabetesPersonFollowUp',
    'visitReasonIsAsthmaPersonFollowUp',
    'visitReasonIsCopdEmphysemaPersonFollowUp',
    'visitReasonIsCancerPatientFollowUp',
    'visitReasonIsOtherChronicDiseasesPersonFollowUp',
    'visitReasonIsPersonWithLeprosyFollowUp',
    'visitReasonIsPersonWithTuberculosisFollowUp',
    'visitReasonIsRespiratorySymptomaticPersonFollowUp',
    'visitReasonIsSmokerFollowUp',
    'visitReasonIsHomeboundBedriddenPersonFollowUp',
    'visitReasonIsSocialVulnerabilityConditionsFollowUp',
    'visitReasonIsBolsaFamiliaConditionalitiesFollowUp',
    'visitReasonIsMentalHealthPersonFollowUp',
    'visitReasonIsAlcoholUserFollowUp',
    'visitReasonIsInvitationToCollectiveActivitiesHealthCampaign',
    'visitReasonIsDischargeFromHospitalization',
    'visitReasonIsEnvironmentalVectorControl',
    'visitReasonIsGuidancePrevention',
    'visitReasonIsOtherDrugsUserFollowUp',
    'visitReasonIsOther',

    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.mothersName',
    'citizenHealth.fathersName',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
    'citizenHealth.countryOfBirth',
    'citizenHealth.birthplaceMunicipality',
    'citizenHealth.birthplaceState',
    'citizenHealth.occupation',
    'citizenHealth.memberOfTraditionalGroupsOrCommunity',
    'citizenHealth.currentWeightStatus',
    'citizenHealth.smoker',
    'citizenHealth.consumesAlcoholicBeverages',
    'citizenHealth.hasAnyDisability',
    'citizenHealth.whichKidneyDisease',
    'citizenHealth.hasRespiratoryDisease',
    'citizenHealth.whichRespiratoryDisease',
    'citizenHealth.hasLeprosy',
    'citizenHealth.hasTuberculosis',
    'citizenHealth.hadCancer',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.receivesBenefit',
  ],
  filterableBy: getZodSchemaInfo(
    shape.pick({
      /* CIDADÃO */
      'citizen.sex': true,
      'citizen.age': true,
      'citizen.deceased': true,

      /* SAÚDE */
      'citizenHealth.isResponsibleFamilyMember': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hadStroke': true,
      'citizenHealth.hadHeartAttack': true,
      'citizenHealth.hasHeartDisease': true,
      'citizenHealth.hasRespiratoryDisease': true,
      'citizenHealth.hadKidneyDisease': true,
      'citizenHealth.hasLeprosy': true,
      'citizenHealth.hasTuberculosis': true,
      'citizenHealth.hadCancer': true,
      'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
      'citizenHealth.hasAnyDisability': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.isBedridden': true,
      'citizenHealth.isDomiciled': true,

      'citizenHealth.smoker': true,
      'citizenHealth.consumesAlcoholicBeverages': true,
      'citizenHealth.takeDrugs': true,
      'citizenHealth.currentWeightStatus': true,
      'citizenHealth.eatHowManyTimesDay': true,

      'citizenHealth.skinColorOrRace': true,
      'citizenHealth.nationality': true,
      'citizenHealth.educationLevel': true,
      'citizenHealth.employmentStatus': true,
      'citizenHealth.sexualOrientation': true,
      'citizenHealth.kinshipWithFamilyResponsible': true,

      'citizenHealth.unknownMother': true,
      'citizenHealth.unknownFather': true,
      'citizenHealth.attendsSchoolOrDaycare': true,

      'citizenHealth.memberOfTraditionalGroupsOrCommunity': true,
      'citizenHealth.takePartInCommunityGroups': true,

      'citizenHealth.homeless': true,
      'citizenHealth.homelessForHowManyTime': true,
      'citizenHealth.receivesBenefit': true,
      'citizenHealth.hasPrivateHealthInsurance': true,

      /* VISITA */
      visitReasonIsRegistrationUpdate: true,
      visitReasonIsPeriodicVisit: true,
      visitReasonIsActiveSearchForConsultation: true,
      visitReasonIsActiveSearchForExamination: true,
      visitReasonIsActiveSearchForVaccination: true,
      visitReasonIsActiveSearchForBolsaFamiliaConditionalities: true,
      visitReasonIsPregnantWomanFollowUp: true,
      visitReasonIsPostpartumWomanFollowUp: true,
      visitReasonIsNewbornFollowUp: true,
      visitReasonIsChildFollowUp: true,
      visitReasonIsMalnutritionPersonFollowUp: true,
      visitReasonIsRehabilitationOrPersonWithDisabilityFollowUp: true,
      visitReasonIsHypertensionPersonFollowUp: true,
      visitReasonIsDiabetesPersonFollowUp: true,
      visitReasonIsAsthmaPersonFollowUp: true,
      visitReasonIsCopdEmphysemaPersonFollowUp: true,
      visitReasonIsCancerPatientFollowUp: true,
      visitReasonIsOtherChronicDiseasesPersonFollowUp: true,
      visitReasonIsPersonWithLeprosyFollowUp: true,
      visitReasonIsPersonWithTuberculosisFollowUp: true,
      visitReasonIsRespiratorySymptomaticPersonFollowUp: true,
      visitReasonIsSmokerFollowUp: true,
      visitReasonIsHomeboundBedriddenPersonFollowUp: true,
      visitReasonIsSocialVulnerabilityConditionsFollowUp: true,
      visitReasonIsBolsaFamiliaConditionalitiesFollowUp: true,
      visitReasonIsMentalHealthPersonFollowUp: true,
      visitReasonIsAlcoholUserFollowUp: true,
      visitReasonIsOtherDrugsUserFollowUp: true,
      visitReasonIsDischargeFromHospitalization: true,
      visitReasonIsEnvironmentalVectorControl: true,
      visitReasonIsInvitationToCollectiveActivitiesHealthCampaign: true,
      visitReasonIsGuidancePrevention: true,
      visitReasonIsOther: true,
      outcomeStatus: true,
    }),
  ),
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(expressionForAssertions),
}

const homeVisitStatsDefinition = defineAction({
  ...homeVisitStatsAction,
  input: z.object({
    variable,
    highlight: expressionForCharacteristics.optional(),
    filter: expressionForCharacteristics.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(shape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default homeVisitStatsDefinition
